// =============================================================================
// SHANIZ GAMING ADMIN - RBAC PERMISSION MATRIX
// =============================================================================

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Save as SaveIcon, RestartAlt as RevertIcon, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminRole, RolePermissions, rolesService } from '../services/api';
import { PermissionButton } from '../components/PermissionButton';
import { PERMISSION_KEYS } from '../hooks/usePermission';

const Roles: React.FC = () => {
  const qc = useQueryClient();
  const [working, setWorking] = useState<Record<string, Record<string, boolean>>>({});
  const [dirty, setDirty] = useState<Record<string, boolean>>({});
  const [snackbar, setSnackbar] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({ open: false, msg: '', sev: 'success' });
  const [addOpen, setAddOpen] = useState(false);
  const [newRole, setNewRole] = useState({ key: '', name: '', description: '' });

  const keys = useQuery({
    queryKey: ['permission-keys'],
    queryFn: rolesService.getPermissionKeys,
  });
  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: rolesService.getAll,
  });

  const allKeys = useMemo(() => {
    const k = keys.data && keys.data.length ? keys.data : PERMISSION_KEYS;
    return Array.from(new Set(k)).sort();
  }, [keys.data]);

  // Resolve the current role list dynamically from the backend
  const allRoles: RolePermissions[] = Array.isArray(roles.data) ? roles.data : [];
  const roleKeys = allRoles.map((r) => r.role);

  useEffect(() => {
    if (!roles.data) return;
    const rows = Array.isArray(roles.data) ? roles.data : [];
    const next: Record<string, Record<string, boolean>> = {};
    for (const r of rows) {
      next[r.role] = { ...(r.permissions || {}) };
    }
    setWorking(next);
    setDirty({});
  }, [roles.data]);

  const save = useMutation({
    mutationFn: ({ role, perms }: { role: string; perms: Record<string, boolean> }) => rolesService.update(role as AdminRole, perms),
    onSuccess: (_d, vars) => {
      setDirty((d) => ({ ...d, [vars.role]: false }));
      qc.invalidateQueries({ queryKey: ['roles'] });
      setSnackbar({ open: true, msg: `${vars.role} saved`, sev: 'success' });
    },
    onError: (e: Error) => setSnackbar({ open: true, msg: e.message || 'Save failed', sev: 'error' }),
  });

  const createRole = useMutation({
    mutationFn: (payload: { key: string; name: string; description?: string }) => rolesService.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roles'] });
      setAddOpen(false);
      setNewRole({ key: '', name: '', description: '' });
      setSnackbar({ open: true, msg: 'Role created', sev: 'success' });
    },
    onError: (e: any) => setSnackbar({ open: true, msg: e?.response?.data?.message?.[0] || e?.response?.data?.message || e.message || 'Create failed', sev: 'error' }),
  });

  const deleteRole = useMutation({
    mutationFn: (role: string) => rolesService.remove(role),
    onSuccess: (_d, role) => {
      qc.invalidateQueries({ queryKey: ['roles'] });
      setSnackbar({ open: true, msg: `${role} deleted`, sev: 'success' });
    },
    onError: (e: any) => setSnackbar({ open: true, msg: e?.response?.data?.message?.[0] || e.message || 'Delete failed', sev: 'error' }),
  });

  const toggle = (role: string, key: string) => {
    setWorking((w) => ({ ...w, [role]: { ...w[role], [key]: !w[role]?.[key] } }));
    setDirty((d) => ({ ...d, [role]: true }));
  };

  const saveAll = () => {
    roleKeys.filter((r) => dirty[r]).forEach((r) => save.mutate({ role: r, perms: working[r] || {} }));
  };

  const revert = () => {
    if (!roles.data) return;
    const rows = Array.isArray(roles.data) ? roles.data : [];
    const next: Record<string, Record<string, boolean>> = {};
    for (const r of rows) {
      next[r.role] = { ...(r.permissions || {}) };
    }
    setWorking(next);
    setDirty({});
  };

  const anyDirty = roleKeys.some((r) => dirty[r]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Roles & Permissions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Toggle permissions per role. Changes only take effect after saving. SUPER_ADMIN cannot be edited.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <PermissionButton
            permission="roles.edit"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setAddOpen(true)}
          >
            Add role
          </PermissionButton>
          <PermissionButton
            permission="roles.edit"
            variant="outlined"
            startIcon={<RevertIcon />}
            disabled={!anyDirty}
            onClick={revert}
          >
            Revert
          </PermissionButton>
          <PermissionButton
            permission="roles.edit"
            variant="outlined"
            color="inherit"
            startIcon={<SaveIcon />}
            disabled={!anyDirty || save.isPending}
            onClick={saveAll}
            sx={{ borderColor: 'divider', color: 'text.primary', '&:hover': { borderColor: 'text.primary', bgcolor: 'action.hover' } }}
          >
            Save changes
          </PermissionButton>
        </Stack>
      </Stack>

      <Card>
        <CardContent>
          {(keys.isError || roles.isError) && <Alert severity="error">Failed to load roles/permissions.</Alert>}
          {roles.isLoading ? (
            <Skeleton variant="rectangular" height={380} />
          ) : (
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: 'background.paper', minWidth: 220 }}>Permission</TableCell>
                    {allRoles.map((rd) => (
                      <TableCell key={rd.role} align="center" sx={{ bgcolor: 'background.paper', whiteSpace: 'nowrap' }}>
                        <Stack alignItems="center" spacing={0.5}>
                          <Typography variant="caption" fontWeight={700}>{rd.role}</Typography>
                          {!rd.isSystem && (
                            <Tooltip title="Delete this custom role">
                              <IconButton size="small" onClick={() => { if (window.confirm(`Delete role ${rd.role}?`)) deleteRole.mutate(rd.role); }}>
                                <DeleteIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {rd.isSystem && <Chip size="small" variant="outlined" label="system" sx={{ height: 18, fontSize: 10 }} />}
                          {dirty[rd.role] && <Chip size="small" color="warning" label="Modified" />}
                        </Stack>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allKeys.map((k) => (
                    <TableRow key={k} hover>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{k}</TableCell>
                      {allRoles.map((rd) => (
                        <TableCell key={rd.role} align="center">
                          <Checkbox
                            checked={Boolean(working[rd.role]?.[k])}
                            disabled={rd.role === 'SUPER_ADMIN'}
                            onChange={() => toggle(rd.role, k)}
                            size="small"
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Add Role Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle>
          <Typography variant="h6" fontWeight={700}>Create custom role</Typography>
          <Typography variant="body2" color="text.secondary">
            A new role starts with view-only permissions. Toggle checkboxes after creating to grant more.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              fullWidth size="small" autoFocus
              label="Role key"
              placeholder="OPS_LEAD"
              helperText="Uppercase letters, numbers, and underscores only (auto-sanitized)."
              value={newRole.key}
              onChange={(e) => setNewRole((r) => ({ ...r, key: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_') }))}
            />
            <TextField
              fullWidth size="small"
              label="Display name"
              placeholder="Ops Lead"
              value={newRole.name}
              onChange={(e) => setNewRole((r) => ({ ...r, name: e.target.value }))}
            />
            <TextField
              fullWidth size="small" multiline minRows={2}
              label="Description (optional)"
              placeholder="What this role is responsible for"
              value={newRole.description}
              onChange={(e) => setNewRole((r) => ({ ...r, description: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setAddOpen(false)} sx={{ color: 'text.secondary' }}>Cancel</Button>
          <Button
            variant="outlined" color="inherit"
            disabled={!newRole.key || !newRole.name || createRole.isPending}
            onClick={() => createRole.mutate({ key: newRole.key, name: newRole.name, description: newRole.description })}
            sx={{ borderColor: 'divider', color: 'text.primary', '&:hover': { borderColor: 'text.primary', bgcolor: 'action.hover' } }}
          >
            {createRole.isPending ? 'Creating…' : 'Create role'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.sev} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Roles;
